<template>
  <BaseWidget
    title="Upcoming bookings"
    :show-info-icon="true"
    info-tooltip="Bookings scheduled in the near future."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/collections/356500-bookings-assignments', text: 'Learn about Bookings' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    custom-class="upcoming-bookings-widget"
    :date-range="dateRange"
    :future-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading && !data"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      Error loading bookings: {{ error.message }}
    </div>
    <template v-else>
      <div
        ref="widgetRef"
        :class="['widget-content-wrapper', { 'focused': focused }]"
      >
        <div class="booking-count">
          <div class="count-row">
            <span class="count">{{ todayCount }}</span>
            <ComparisonChip 
              :change="changePercentage" 
              :previous-value="formattedPreviousCount"
              :previous-period-dates="previousTimestamps"
              :date-range-preset="dateRange"
              label="Upcoming bookings"
            />
          </div>
          <div class="count-label-row">
            <span class="count-label">{{ dateRangeLabel }}</span>
            <a
              v-if="bookings.length > 0"
              class="focused-view-all-link"
              @click.prevent="handleViewAllClick"
            >
              View all
            </a>
          </div>
        </div>
        
        <div
          v-if="!focused"
          class="bookings-list"
        >
          <div 
            v-for="(booking, index) in displayBookings" 
            :key="booking.id || index" 
            class="booking-item"
            @click="handleBookingClickGuarded(booking)"
          >
            <div class="booking-row">
              <div class="booking-image">
                <img 
                  v-if="booking.image" 
                  :src="booking.image" 
                  :alt="booking.name"
                >
                <div
                  v-else
                  class="image-placeholder"
                >
                  {{ getInitials(booking.name) }}
                </div>
              </div>
              <div class="booking-details">
                <div class="booking-name">
                  {{ booking.name }}
                </div>
                <div class="booking-meta">
                  <span class="booking-time">{{ formatBookingTime(booking) }}</span>
                  <span
                    v-if="booking.user?.name"
                    class="booking-meta-sep"
                  > • </span>
                  <span
                    v-if="booking.user?.name"
                    class="booking-account-name"
                  >{{ booking.user.name }}</span>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="displayBookings.length === 0 && bookings.length === 0 && !hasMoreBookings"
            class="no-bookings"
          >
            No upcoming bookings
          </div>
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'

import ComparisonChip from './ComparisonChip.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import { useGraphQLData } from '../composables/useWidgetData.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps, DATE_RANGE_PRESETS, DATE_RANGE_LABELS, getDateRangeDates, formatDateYMD } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { getResourceImageUrl } from '../utils/resourceImage.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { getDemoUpcomingBookingsData } from '../services/demoData.js'

const props = defineProps({
  editMode: {
    type: Boolean,
    default: false
  },
  rowSpan: {
    type: Number,
    default: 2
  },
  colSpan: {
    type: Number,
    default: 1
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  priority: {
    type: Number,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'next_7_days')
const widgetRef = ref(null)

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Next 7 days'
})

// Get location IDs from parent window URL
const locationIds = useLocationFilter()

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Calculate timestamps from date range
const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
})

// Query bookings only (type: BOOKING) with full details for display
// Uses start_timestamp_from/to so we only get bookings that START within the range
const bookingsQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_from: $from_timestamp
      start_timestamp_to: $to_timestamp
      limit: $limit
      page: $page
      type: BOOKING
      status: [ACTIVE, UPCOMING]
    ) {
      total
      data {
        booking_id
        title
        start_timestamp
        end_timestamp
        resource {
          resource_id
          name
          main_image {
            url
            __typename
          }
          location {
            location_id
            name
            timezone
            timezone_name
            __typename
          }
          __typename
        }
        location {
          location_id
          name
          timezone
          timezone_name
          __typename
        }
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const { data, loading, error, cleanup, refresh } = useGraphQLData({
  dataKey: 'bookings_display',
  query: bookingsQuery,
  variables: computed(() => ({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp,
    limit: 5,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: props.priority ?? 2,
  autoFetch: true,
  getDemoData: () => getDemoUpcomingBookingsData({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp
  })
})

// Fetch previous period data for comparison
const previousTimestamps = computed(() => {
  return getPreviousPeriodTimestamps(dateRange.value, referenceDate.value)
})

const previousQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_from: $from_timestamp
      start_timestamp_to: $to_timestamp
      limit: $limit
      page: $page
      type: BOOKING
      status: [ACTIVE, UPCOMING]
    ) {
      total
      __typename
    }
  }
`

const {
  data: previousData,
  loading: previousLoading,
  error: previousError,
  cleanup: previousCleanup,
  refresh: refreshPrevious
} = useGraphQLData({
  dataKey: 'bookings_previous',
  query: previousQuery,
  variables: computed(() => ({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp,
    limit: 1,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  })),
  priority: props.priority ?? 2,
  autoFetch: true,
  getDemoData: () => getDemoUpcomingBookingsData({
    from_timestamp: previousTimestamps.value.from_timestamp,
    to_timestamp: previousTimestamps.value.to_timestamp
  })
})

// Ensure previous data is fetched when current data loads
watch(() => data.value, (newData) => {
  if (newData && !previousLoading.value && !previousData.value) {
    refreshPrevious(true)
  }
}, { immediate: true })

// Map schedule data to display format (API already filters by type: BOOKING)
const bookings = computed(() => {
  const raw = data.value?.schedule
  if (!raw?.data) {
    return []
  }

  return raw.data
    .map(item => {
      // Get timezone from location (prefer direct location, fallback to resource location)
      const location = item.location || item.resource?.location
      const timezone = location?.timezone || null

      return {
        id: item.booking_id,
        name: item.resource?.name || item.title || 'Unnamed Booking',
        time: item.start_timestamp * 1000, // Convert to milliseconds
        endTime: item.end_timestamp * 1000,
        image: getResourceImageUrl(item.resource?.main_image?.url, item.resource?.name || item.title || 'Booking'),
        location: location?.name || null,
        timezone: timezone, // Include timezone for formatting
        user: item.owner_account ? {
          name: item.owner_account.name || null,
          avatar: getAvatarUrl(item.owner_account.avatar?.url, item.owner_account.name || item.owner_account.account_id)
        } : null
      }
    })
    .sort((a, b) => a.time - b.time) // Sort by start time
})

// Count bookings in the selected date range (API total is accurate since query filters by start date)
const todayCount = computed(() => {
  return data.value?.schedule?.total ?? bookings.value.length
})

// Count bookings in previous period (from API total, already filtered by type: BOOKING)
const previousCount = computed(() => {
  if (previousData.value === null || previousData.value === undefined) return null
  return previousData.value?.schedule?.total ?? 0
})

// Calculate percentage change
const changePercentage = computed(() => {
  const current = todayCount.value
  const previous = previousCount.value
  
  // Only show comparison if we have both current and previous data loaded
  // Don't show if data is still loading or hasn't loaded yet
  if (loading.value || previousLoading.value) {
    return null
  }
  
  if (previousData.value === null || previousData.value === undefined) {
    return null
  }
  
  // If previous is 0 and current is also 0, don't show comparison
  if (previous === 0 && current === 0) return null
  
  // If previous is 0 but current has value, show as 100% increase
  if (previous === 0 && current > 0) {
    return 100 // This will show as 100% increase
  }
  
  // Calculate percentage change
  const change = ((current - previous) / previous) * 100
  const roundedChange = Math.round(change * 10) / 10 // Round to 1 decimal place
  
  return roundedChange
})

// Format previous count for display
const formattedPreviousCount = computed(() => {
  if (previousCount.value === null || previousCount.value === undefined) return null
  return previousCount.value.toString()
})

// Calculate number of rows to display based on widget height
// Calculate number of rows to display based on widget height (64px rows; leave room for "View more")
// Pattern: 2-row→2 slots, 3-row→4, 4-row→6, 5-row→8, 6-row→10... i.e. 2*(rowSpan-1)
const maxRowsToDisplay = computed(() => {
  if (props.rowSpan === 1) return 0
  return 2 * (props.rowSpan - 1)
})

// Check if there are more bookings than we can display (only show when rowSpan > 1)
const hasMoreBookings = computed(() => {
  return props.rowSpan > 1 && todayCount.value > maxRowsToDisplay.value
})

// Calculate remaining count
const remainingBookingsCount = computed(() => {
  if (!hasMoreBookings.value) return 0
  return todayCount.value - displayBookings.value.length
})

// Display upcoming bookings based on widget height (leave room for "more" row if needed)
const displayBookings = computed(() => {
  if (maxRowsToDisplay.value === 0) {
    return []
  }
  return bookings.value.slice(0, maxRowsToDisplay.value)
})

// Format booking time for display using the location's timezone
const formatBookingTime = (booking) => {
  if (!booking.time) return ''
  
  // Use the location's timezone if available and valid, otherwise fall back to browser timezone
  const timezone = (booking.timezone && typeof booking.timezone === 'string' && booking.timezone.trim()) || undefined
  
  // Get user's locale timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  
  // Create date formatters with the location's timezone
  const timeFormatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timezone
  })
  
  const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: timezone
  })
  
  const dateFormatterForComparison = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone
  })
  
  const bookingDate = new Date(booking.time)
  
  // Only show timezone when it differs from user's. Compare by UTC offset at this date (reliable;
  // abbreviation can vary: same zone may show "PST" vs "GMT-8" and falsely differ)
  let showTimezoneIndicator = false
  let timezoneFormatter = null
  if (timezone) {
    try {
      const locParts = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'shortOffset' }).formatToParts(bookingDate)
      const userParts = new Intl.DateTimeFormat('en-US', { timeZone: userTimezone, timeZoneName: 'shortOffset' }).formatToParts(bookingDate)
      const locOffset = locParts.find(p => p.type === 'timeZoneName')?.value || ''
      const userOffset = userParts.find(p => p.type === 'timeZoneName')?.value || ''
      showTimezoneIndicator = locOffset !== userOffset
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    } catch (_) {
      showTimezoneIndicator = timezone !== userTimezone
      if (showTimezoneIndicator) {
        timezoneFormatter = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      }
    }
  }
  
  // Get date parts in the location's timezone for comparison
  const bookingDateParts = dateFormatterForComparison.formatToParts(bookingDate)
  const bookingYear = parseInt(bookingDateParts.find(p => p.type === 'year').value)
  const bookingMonth = parseInt(bookingDateParts.find(p => p.type === 'month').value) - 1 // Month is 0-indexed
  const bookingDay = parseInt(bookingDateParts.find(p => p.type === 'day').value)
  
  // Get today's date in the location's timezone
  const now = new Date()
  const todayParts = dateFormatterForComparison.formatToParts(now)
  const todayYear = parseInt(todayParts.find(p => p.type === 'year').value)
  const todayMonth = parseInt(todayParts.find(p => p.type === 'month').value) - 1
  const todayDay = parseInt(todayParts.find(p => p.type === 'day').value)
  
  const bookingDateObj = new Date(bookingYear, bookingMonth, bookingDay)
  const todayDateObj = new Date(todayYear, todayMonth, todayDay)
  
  const diffTime = bookingDateObj - todayDateObj
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  
  // Format time using the location's timezone
  let timeStr = timeFormatter.format(bookingDate)
  
  // Add timezone indicator if different from user's timezone
  if (showTimezoneIndicator && timezoneFormatter) {
    const timezoneParts = timezoneFormatter.formatToParts(bookingDate)
    const timezoneName = timezoneParts.find(p => p.type === 'timeZoneName')?.value || ''
    if (timezoneName) {
      timeStr = `${timeStr} (${timezoneName})`
    }
  }
  
  if (diffDays === 0) {
    return `Today @ ${timeStr}`
  } else if (diffDays === 1) {
    return `Tomorrow @ ${timeStr}`
  } else {
    const dateStr = dateFormatter.format(bookingDate)
    return `${dateStr} @ ${timeStr}`
  }
}

// Get initials for placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Extract parameters from URL for parent window communication
const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  return {
    client_id: urlParams.get('client_id') || urlParams.get('clientId') || '',
    canvasUUID: urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || '',
    token: urlParams.get('token') || urlParams.get('access_token') || '',
    organization_id: urlParams.get('orgId') || urlParams.get('organization_id') || '',
    subdomain: urlParams.get('subdomain') || '',
    locations: urlParams.get('locations') || '',
    email: urlParams.get('email') || '',
    current_user_id: urlParams.get('current_user_id') || urlParams.get('userId') || '',
    optix_placement: urlParams.get('optix-placement') || urlParams.get('optix_placement') || 'float',
    optix_mobile: urlParams.get('optix-mobile') || urlParams.get('optix_mobile') || 'false'
  }
}

// Handle "View all" click - navigate to schedule page via parent window
const { guard } = useTrialModeGuard()
const handleViewAllClick = () => {
  const params = getUrlParams()
  
  // Try to get client_id and canvasUUID from various sources
  let clientId = params.client_id
  let canvasUUID = params.canvasUUID
  
  // Check document.referrer for parameters
  if (document.referrer) {
    try {
      const referrerUrl = new URL(document.referrer)
      const referrerParams = new URLSearchParams(referrerUrl.search)
      if (!clientId) {
        clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      }
      if (!canvasUUID) {
        canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
      }
      if (clientId || canvasUUID) {
      }
    } catch (e) {
    }
  }
  
  // Try to get from parent window's URL if accessible (may fail due to CORS)
  if (!clientId || !canvasUUID) {
    try {
      if (window.parent && window.parent !== window) {
        const parentUrl = window.parent.location.href
        const parentUrlObj = new URL(parentUrl)
        const parentParams = new URLSearchParams(parentUrlObj.search)
        if (!clientId) {
          clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
        }
        if (!canvasUUID) {
          canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
        }
        if (clientId || canvasUUID) {
        }
      }
    } catch (e) {
      // CORS - can't access parent location
    }
  }
  
  // Set up listener to see if parent responds or sends any messages
  const messageListener = (event) => {
    if (event.data && typeof event.data === 'object') {
    }
  }
  window.addEventListener('message', messageListener)
  
  // Remove listener after 10 seconds
  setTimeout(() => {
    window.removeEventListener('message', messageListener)
  }, 10000)
  
  // Send message immediately - try with and without parameters
  // Build message matching engineer's example format exactly
  const dates = getDateRangeDates(dateRange.value)
  // Optix schedule list treats date_to as exclusive, so pass the day after the range ends
  const [toYear, toMonth, toDay] = dates.to.split('-').map(Number)
  const nextDay = new Date(toYear, toMonth - 1, toDay + 1)
  const dateTo = formatDateYMD(nextDay)
  const navigationUrl = `/schedule/list?event_types=BOOKING&date_from=${dates.from}&date_to=${dateTo}`
  
  // canvasUUID is required and must be non-empty (can be any string)
  // If not in URL, use a default value
  const finalCanvasUUID = canvasUUID || "default-canvas-uuid"
  
  // Log the URL prominently
  
  const message = {
    command: "ReplaceUrl",
    payload: {
      client_id: clientId || "",
      canvasUUID: finalCanvasUUID,
      url: navigationUrl,
      reload: true
    }
  }
  
  
  try {
    window.parent.postMessage(message, "*")
    
    // Also try window.top in case of nested iframes
    if (window.top && window.top !== window && window.top !== window.parent) {
      window.top.postMessage(message, "*")
    }
    
    // Note: canvasUUID is now always set (required by parent window)
    // client_id is optional but included if available
  } catch (error) {
    console.error('❌ Error posting message:', error)
  }
  
  // Log final parameters being used
  
}

// Handle individual booking click - open booking panel via parent window
// Uses ViewDetails command format matching the working Optix scheduler
const handleBookingClickGuarded = guard((booking) => {
  const params = getUrlParams()
  
  if (!booking.id) {
    console.warn('⚠️ Booking ID not found:', booking)
    return
  }
  
  // Extract booking_id - handle both direct id and prefixed formats (e.g., "BOOKING-123" → "123")
  let bookingId = booking.id
  if (bookingId.includes('-')) {
    // If id has a prefix like "BOOKING-123", extract just the number
    const parts = bookingId.split('-')
    bookingId = parts[parts.length - 1]
  }
  
  
  // Use ViewDetails command format (matching working Optix scheduler)
  // Command: "ViewDetails"
  // Payload: { booking_id: "...", canvasUUID: "..." }
  const message = {
    command: "ViewDetails",
    payload: {
      booking_id: bookingId,
      canvasUUID: params.canvasUUID || "xxx" // Use actual canvasUUID if available, otherwise "xxx" as placeholder
    }
  }
  
  
  // Send message to parent Optix window (matching working app pattern)
  window.parent.postMessage(message, "*")
  
  
  // Note: canvasUUID is currently using placeholder "xxx" if not in URL
  // The parent window should handle the ViewDetails command and open the booking panel
  if (!params.canvasUUID) {
    console.warn('⚠️ canvasUUID not found in URL - using placeholder "xxx"')
    console.warn('💡 Update canvasUUID extraction if it\'s available from another source')
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
  }
})

// Watch for date range changes and refresh data
watch(dateRange, () => {
  refresh(true)
})

// Debug: Log comparison data
watch([todayCount, previousCount, changePercentage], ([current, previous, change]) => {
  if (process.env.NODE_ENV === 'development') {
  }
}, { immediate: true })

onUnmounted(() => {
  cleanup()
  previousCleanup()
})
</script>

<style scoped>
:deep(.upcoming-bookings-widget) {
  cursor: pointer;
  overflow: hidden;
}

:deep(.upcoming-bookings-widget .widget-content) {
  justify-content: flex-start;
  overflow: hidden;
}


.booking-count {
  display: flex;
  flex-direction: column;
  margin-bottom: 2px;
}

.count-row {
  display: flex;
  align-items: baseline;
  margin-bottom: 6px;
}

.count {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.count-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
  padding-bottom: 14px;
}

.count-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.widget-content-wrapper {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.widget-content-wrapper.focused {
  position: relative;
}

.focused-view-all-link {
  font-size: 13px;
  flex-shrink: 0;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.2s ease;
}

.focused-view-all-link:hover {
  color: #2563eb;
}

.bookings-list {
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow: visible;
  min-height: 0;
  max-height: none;
}

.booking-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  box-sizing: border-box;
}

.booking-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.booking-item-more {
  min-height: unset;
  padding: 12px 4px;
}

.booking-item-more:hover {
  background-color: transparent;
}

.booking-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;
}

.booking-image {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.booking-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.booking-details {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.booking-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
}

.booking-meta {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 2px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.booking-meta-sep {
  color: rgba(0, 0, 0, 0.87);
  margin: 0 4px;
}

.booking-account-name {
  color: rgba(0, 0, 0, 0.87);
}

.booking-time {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
}

.booking-item-more .booking-row {
  width: 100%;
}

.booking-item-more .booking-row-more {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}

.booking-item-more .view-more-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  transition: color 0.2s ease;
}

.booking-item-more .view-more-link:hover {
  color: #2563eb;
}

.booking-item-more .booking-details {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.booking-item-more .booking-name {
  margin-bottom: 0;
}

.booking-item-more .booking-time {
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-bookings {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.booking-image-more {
  position: relative;
  width: 36px;
  height: 28px;
  background: transparent;
}

.stacked-images {
  position: relative;
  width: 100%;
  height: 100%;
}

.stacked-image {
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  overflow: hidden;
  border: 2px solid white;
}

.stacked-image-top {
  top: 0;
  left: 0;
  z-index: 2;
  transform: rotate(-8deg);
}

.stacked-image-bottom {
  top: 0;
  left: 7px;
  z-index: 1;
}

.stacked-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

</style>
